import { exportDataGrid as exportToExcel } from 'devextreme/excel_exporter';
import { exportDataGrid as exportToPdf } from 'devextreme/pdf_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import notify from 'devextreme/ui/notify';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

/**
 * Exportación estándar de cualquier dx-data-grid a Excel y a PDF.
 *
 * Los booleanos NO se exportan como true/false (ni como "verdadero/falso", que es lo que
 * escribe DevExtreme cuando el navegador está en español): se normalizan SIEMPRE a
 * "Yes" / "No" en inglés, igual que se ven en pantalla.
 */

/**
 * Un export que falla NO puede quedar en silencio: antes solo iba a console.error y en
 * pantalla no pasaba nada (el usuario clickeaba el icono y no bajaba ningun archivo, sin
 * ninguna pista de por que). Ahora ademas avisa con el toast de DevExtreme.
 */
function avisarFallo(tipo: string, err: any): void {
  const detalle = (err && (err.message || err.description)) ? ': ' + (err.message || err.description) : '';
  console.error('Export a ' + tipo + ' fallido', err);
  try { notify('Export to ' + tipo + ' failed' + detalle, 'error', 6000); } catch { /* noop */ }
}

/** Valor que exceljs SI puede escribir. Un objeto/array en una celda hace que
 *  workbook.xlsx.writeBuffer() reviente y —al no tener handler— el archivo nunca bajaba. */
function valorSeguro(v: any): any {
  if (v === null || v === undefined) { return v; }
  const t = typeof v;
  if (t === 'string' || t === 'number' || t === 'boolean') { return v; }
  if (v instanceof Date) { return v; }
  try { return String(v); } catch { return ''; }
}

/** Texto que debe salir en el archivo para un valor de celda. */
function cellText(value: any): string | null {
  const yesNo = boolToYesNo(value);
  if (yesNo !== null) { return yesNo; }
  return null;   // null = no tocar, se exporta el valor tal cual
}

/**
 * Devuelve 'Yes'/'No' si el valor es un booleano (o una de sus variantes de texto que
 * llegan del backend / de la localización), o null si no es booleano.
 */
export function boolToYesNo(value: any): string | null {
  if (value === true)  { return 'Yes'; }
  if (value === false) { return 'No'; }
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true'  || v === 'verdadero' || v === 'sí' || v === 'si') { return 'Yes'; }
    if (v === 'false' || v === 'falso')                                { return 'No'; }
  }
  return null;
}

/**
 * El widget, venga como `instance` o como componente de devextreme-angular.
 *
 * 🔴🔴 OJO con el desenvoltorio: el WIDGET de DevExtreme tiene un **metodo** `instance()`
 * (core/widget/component.js: `instance() { return this }`). La version anterior hacia
 * `g.instance ? g.instance : g`, asi que cuando se le pasaba el widget —que es lo que hacen
 * la directiva [spGrid] y los ~96 call sites con `this.xGrid.instance`— devolvia LA FUNCION.
 * El exportador la rechazaba con "The 'component' field must contain a DataGrid instance"
 * y, al no haber handler de error, el click no hacia nada. Era el bug de fondo de los
 * reportes de QA: "muchos grids no exportan".
 *
 * Regla: si el objeto ya tiene NAME (lo setea el constructor de Component con el nombre
 * registrado, ej. 'dxDataGrid') YA es el widget. Solo se desenvuelve cuando `instance` es
 * un OBJETO, que es el caso del componente Angular.
 */
function widget(g: any): any {
  if (!g) { return g; }
  if (typeof g.NAME === 'string') { return g; }                              // ya es el widget
  if (g.instance && typeof g.instance === 'object') { return g.instance; }   // componente Angular
  return g;
}

/**
 * Plan B: arma el .xlsx leyendo las columnas y filas del grid, SIN el exportador de
 * DevExtreme. Existe porque cuando exportDataGrid() rechaza (o exceljs no puede escribir
 * el buffer) el usuario no recibia NADA: clickeaba el icono y no pasaba nada. Con esto
 * igual baja el archivo con lo que se ve en pantalla (filtro y orden aplicados).
 */
function exportManual(component: any, fileName: string, sheetName: string, motivo: any): void {
  try {
    const cols = (component.getVisibleColumns() || []).filter((c: any) =>
      c && c.dataField && c.allowExporting !== false && c.type !== 'buttons' && c.command == null);
    if (!cols.length) { avisarFallo('Excel', motivo); return; }

    // Las filas: primero las del dataSource (filtradas + ordenadas y sin el limite del
    // scroll virtual) y si no hay, las visibles.
    let filas: any[] = [];
    try {
      const ds = component.getDataSource();
      filas = (ds && typeof ds.items === 'function') ? (ds.items() || []) : [];
    } catch { filas = []; }
    if (!filas.length) {
      filas = (component.getVisibleRows() || [])
        .filter((r: any) => r && r.rowType === 'data').map((r: any) => r.data);
    }
    // Los grupos vienen como {items:[...]}: se aplanan para no perder filas.
    const planas: any[] = [];
    const aplanar = (arr: any[]) => arr.forEach((it: any) => {
      if (it && Array.isArray(it.items)) { aplanar(it.items); } else if (it) { planas.push(it); }
    });
    aplanar(filas);

    const wb = new Workbook();
    const ws = wb.addWorksheet(sanitizeSheetName(sheetName || fileName));
    ws.addRow(cols.map((c: any) => c.caption || c.dataField));
    ws.getRow(1).font = { bold: true };
    planas.forEach((row: any) => {
      ws.addRow(cols.map((c: any) => {
        let v = row ? row[c.dataField] : null;
        if (v === undefined && c.dataField.indexOf('.') > 0) {       // dataField anidado
          v = c.dataField.split('.').reduce((o: any, k: string) => (o == null ? o : o[k]), row);
        }
        const yn = boolToYesNo(v);
        return yn !== null ? yn : valorSeguro(v);
      }));
    });
    wb.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), withExt(fileName, 'xlsx'));
      console.warn('Export a Excel: se uso el plan B (el exportador de DevExtreme fallo)', motivo);
    }, (e: any) => avisarFallo('Excel', e));
  } catch (e) {
    avisarFallo('Excel', motivo || e);
  }
}

/** Exporta el grid a .xlsx respetando filtro, orden y columnas visibles. */
/**
 * @param postProceso hook OPCIONAL sobre la hoja ya escrita (encabezado en negrita, fila
 *   congelada, anchos...). Existe para que una pantalla que necesita formato extra no tenga
 *   que volver a armar el export a mano y perder la normalizacion de booleanos a Yes/No.
 */
export function exportGridToExcel(grid: any, fileName: string, sheetName?: string, onDone?: () => void,
                                  postProceso?: (worksheet: any, workbook: any) => void): void {
  const component = widget(grid);
  if (!component) { if (onDone) { onDone(); } return; }
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(sanitizeSheetName(sheetName || fileName));

  // El exportador de DX puede tirar de forma SINCRONICA (no solo rechazar la promesa):
  // ahi tambien tiene que entrar el plan B, si no el click vuelve a no hacer nada.
  try {
  exportToExcel({
    component,
    worksheet,
    autoFilterEnabled: true,
    customizeCell: (options: any) => {
      const { gridCell, excelCell } = options;
      if (!gridCell || gridCell.rowType !== 'data') { return; }
      const txt = cellText(gridCell.value);
      if (txt !== null) {
        excelCell.value = txt;
        excelCell.alignment = { horizontal: 'center' };
        return;
      }
      const seguro = valorSeguro(gridCell.value);
      if (seguro !== gridCell.value) { excelCell.value = seguro; }
    },
  }).then(() => {
    if (postProceso) { try { postProceso(worksheet, workbook); } catch { /* el export no se cae por el formato */ } }
    workbook.xlsx.writeBuffer().then((buffer) => {
      try {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), withExt(fileName, 'xlsx'));
      } catch (e) { avisarFallo('Excel', e); }
      if (onDone) { onDone(); }
    }, (err: any) => {
      // ESTE handler no existia: si exceljs no podia escribir el buffer, no bajaba nada
      // y no se veia ni un error (celda con objeto/array, hoja invalida...).
      exportManual(component, fileName, sheetName || fileName, err);
      if (onDone) { onDone(); }
    });
  }, (err: any) => {
    exportManual(component, fileName, sheetName || fileName, err);
    if (onDone) { onDone(); }
  });
  } catch (e) {
    exportManual(component, fileName, sheetName || fileName, e);
    if (onDone) { onDone(); }
  }
}

/** Exporta el grid a .pdf (apaisado) respetando filtro, orden y columnas visibles. */
export function exportGridToPdf(grid: any, fileName: string, title?: string, onDone?: () => void): void {
  const component = widget(grid);
  if (!component) { if (onDone) { onDone(); } return; }

  /* 🔴 El ANCHO de la hoja sale de la cantidad de columnas, no siempre A4.
     Con A4 apaisado (842pt) y 17 columnas quedaban ~48pt por columna: a 10pt de fuente
     entran 6 o 7 caracteres, asi que DevExtreme partia CADA celda en varias lineas, las
     filas se volvian enormes y el reporte salia en 67 paginas para 523 registros,
     ilegible. Es la "falla de formato PDF" que reporto QA en Service Work Orders.
     Ahora la hoja crece a razon de ~78pt utiles por columna (A4 sigue igual hasta 10
     columnas, que es la mayoria de los grids) y la letra baja a 7-8pt. */
  const columnas = contarColumnas(component);
  const ALTO_A4 = 595;                       // A4 apaisado: 842 x 595
  const ancho = Math.max(842, Math.min(columnas * 78 + 40, 3400));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: [ancho, ALTO_A4] });
  const tamFuente = columnas > 12 ? 7 : (columnas > 8 ? 8 : 9);
  const topMargin = title ? 54 : 30;

  /* 🔴 exportDataGrid de devextreme/pdf_exporter SOLO acepta un DataGrid: con un
     dx-tree-list rechaza con "The 'component' field must contain a DataGrid instance."
     y —al no haber handler— el click no bajaba nada (visto en /engUtility, tabs System
     Types y Regions, que son treelists). DevExtreme 24.2 no trae exportador PDF de
     TreeList, asi que se arma la tabla a mano con jspdf-autotable. */
  if (component.NAME === 'dxTreeList') {
    pdfDeTreeList(component, doc, fileName, title, topMargin, onDone);
    return;
  }

  exportToPdf({
    jsPDFDocument: doc,
    component,
    topLeft: { x: 12, y: topMargin },
    columnWidths: undefined,
    customizeCell: (options: any) => {
      const { gridCell, pdfCell } = options;
      if (!gridCell) { return; }
      if (gridCell.rowType === 'header') {
        pdfCell.backgroundColor = '#f1f5f9';
        pdfCell.textColor = '#104b85';
        pdfCell.font = { ...(pdfCell.font || {}), size: tamFuente, style: 'bold' };
        return;
      }
      if (gridCell.rowType !== 'data') { return; }
      pdfCell.font = { ...(pdfCell.font || {}), size: tamFuente };
      const txt = cellText(gridCell.value);
      if (txt !== null) {
        pdfCell.text = txt;
        pdfCell.horizontalAlign = 'center';
      }
    },
  }).then(() => {
    if (title) {
      doc.setFontSize(13);
      doc.setTextColor('#104b85');
      doc.text(title, 12, 34);
    }
    doc.save(withExt(fileName, 'pdf'));
    if (onDone) { onDone(); }
  }, (err: any) => {
    avisarFallo('PDF', err);
    if (onDone) { onDone(); }
  });
}

/** Excel no acepta / \ ? * [ ] ni más de 31 caracteres en el nombre de la hoja. */
function sanitizeSheetName(name: string): string {
  return (name || 'Sheet1').replace(/[\/\\\?\*\[\]:]/g, ' ').trim().substring(0, 31) || 'Sheet1';
}

function withExt(fileName: string, ext: string): string {
  const clean = (fileName || 'export').replace(/\.(xlsx|pdf)$/i, '');
  return clean + '.' + ext;
}

/** Cuantas columnas de datos se van a exportar (las visibles, sin la de comandos). */
function contarColumnas(component: any): number {
  try {
    const cols = component.getVisibleColumns() || [];
    const n = cols.filter((c: any) => c.dataField && c.visible !== false).length;
    return n > 0 ? n : 8;
  } catch (e) {
    return 8;
  }
}

/**
 * PDF de un dx-tree-list: mismas columnas visibles y mismas filas visibles que en
 * pantalla, con la jerarquia marcada por sangria en la primera columna.
 */
function pdfDeTreeList(tl: any, doc: any, fileName: string, title: string,
                       topMargin: number, onDone?: () => void): void {
  try {
    const columnas = (tl.getVisibleColumns() || [])
      .filter((c: any) => c.dataField && c.visible !== false);
    const filas = (tl.getVisibleRows() || []).filter((r: any) => r.rowType === 'data');

    const cabecera = columnas.map((c: any) => c.caption || c.dataField);
    const cuerpo = filas.map((r: any) => columnas.map((c: any, i: number) => {
      let v = r.data ? r.data[c.dataField] : '';
      const yesNo = boolToYesNo(v);
      if (yesNo !== null) { v = yesNo; }
      if (v === null || v === undefined) { v = ''; }
      let texto = String(v);
      // la jerarquia se marca con sangria en la PRIMERA columna
      if (i === 0 && r.level > 0) { texto = '    '.repeat(r.level) + texto; }
      return texto;
    }));

    (doc as any).autoTable({
      head: [cabecera],
      body: cuerpo,
      startY: topMargin,
      margin: { left: 12, right: 12 },
      styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
      headStyles: { fillColor: [241, 245, 249], textColor: [16, 75, 133], fontStyle: 'bold' },
    });

    if (title) {
      doc.setFontSize(13);
      doc.setTextColor('#104b85');
      doc.text(title, 12, 34);
    }
    doc.save(withExt(fileName, 'pdf'));
  } catch (err) {
    avisarFallo('PDF', err);
  }
  if (onDone) { onDone(); }
}
